/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { Grid, Icon, Popover } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftPagination from "components/SoftPagination";
import DataTable from "../Tables/DataTable";

/* 🔹 FILTERS */
import UserFilters from "layouts/interviewSelected/components/useFilters";
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions"; // ✅ new hook

/* 🔹 CALENDAR */
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

import { CheckCircle, Cancel } from "@mui/icons-material";

import SoftInput from "components/SoftInput";

/* 🔹 DRAWER */
import OvertimeDrawer from "./OvertimeDrawer";

/* 🔹 STORE */
import useOvertimeStore from "store/useOvertimeStore";

/* 🔐 AUTH */
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";
import { getLeaveStatusInfo } from "layouts/leavetracker/helper";
import FlagLoader from "layouts/flagloader";

/* ================= DEBOUNCE ================= */
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ================= CLEAN PARAMS ================= */
const cleanParams = (obj) => {
  const p = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== "" && v !== "all" && v !== null && v !== undefined) {
      p[k] = v;
    }
  });
  return p;
};

const PERMISSIONS = {
  ADD_OVERTIME: "Attendance.Overtime.History.AddOvertimeButton",
  BRANCH_FILTER: "Attendance.Overtime.History.BranchFilter",
  SHIFT_FILTER: "Attendance.Overtime.History.ShiftFilter",
  DEPARTMENT_FILTER: "Attendance.Overtime.History.DepartmentFilter",
  DESIGNATION_FILTER: "Attendance.Overtime.History.DesignationFilter",
  ROLE_FILTER: "Attendance.Overtime.History.RoleFilter",
  SEARCH_FILTER: "Attendance.Overtime.History.SearchFilter",
  FROM_DATE_FILTER: "Attendance.Overtime.History.FromDateFilter",
  TO_DATE_FILTER: "Attendance.Overtime.History.ToDateFilter",
  STATUS_FILTER: "Attendance.Overtime.History.StatusFilter",
};

function OvertimeRequest() {
  /* ================= AUTH ================= */
  const { userPermission = [] } = useAuth();

  const canCreateOvertime = hasPermission(userPermission, PERMISSIONS.ADD_OVERTIME);
  const canViewPage = true;
  const canUseStatusFilter = true;
  const canUseFromDate = true;
  const canUseToDate = true;

  /* ================= STORE ================= */
  const { overtimeRequests, fetchOvertimeRequests, approveRequest, rejectRequest, actionLoading } =
    useOvertimeStore();

  /* ================= DROPDOWNS ================= */
  // ✅ NEW: useDropdownOptions returns lazy-loaded options and fetch functions
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchSubBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  /* ================= DRAWER ================= */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [approveReason, setApproveReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const openApprove = (id) => {
    setSelectedRequestId(id);
    setApproveReason("");
    setOpenApproveDialog(true);
  };

  const openReject = (id) => {
    setSelectedRequestId(id);
    setRejectReason("");
    setOpenRejectDialog(true);
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = dateString.includes("T")
        ? new Date(dateString)
        : new Date(dateString + "T00:00:00");
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  }, []);

  const formatMinutesToHours = useCallback((minutes) => {
    if (!minutes && minutes !== 0) return "0h";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }, []);

  const handleApproveConfirm = async () => {
    if (!approveReason.trim()) return;
    const res = await approveRequest(selectedRequestId, approveReason);
    if (res?.success) {
      setOpenApproveDialog(false);
      fetchOvertime(true);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) return;
    const res = await rejectRequest(selectedRequestId, rejectReason);
    if (res?.success) {
      setOpenRejectDialog(false);
      fetchOvertime(true);
    }
  };

  const getStatusColor = useCallback((status) => {
    if (!status) return "text";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      case "cancelled":
        return "secondary";
      default:
        return "text";
    }
  }, []);

  /* ================= FILTER STATE ================= */
  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "all",
    start_date: "",
    end_date: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  /* ================= PAGINATION ================= */
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  /* ================= LOADING ================= */
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  /* ================= CALENDAR ================= */
  const [fromAnchor, setFromAnchor] = useState(null);
  const [toAnchor, setToAnchor] = useState(null);

  const lastQueryRef = useRef("");

  const formatDateTime = useCallback((dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  }, []);

  /* ================= FETCH ================= */
  const fetchOvertime = async (initial = false) => {
    const params = cleanParams({
      ...filters,
      q: debouncedSearch,
      page: pagination.current_page,
      per_page: pagination.per_page,
    });

    const key = JSON.stringify(params);
    lastQueryRef.current = key;

    try {
      initial ? setPageLoading(true) : setTableLoading(true);

      const res = await fetchOvertimeRequests(params);

      const meta = res?.meta || res; // API might return paginator at root

      if (meta && meta.current_page) {
        setPagination((p) => {
          if (
            p.current_page === meta.current_page &&
            p.last_page === meta.last_page &&
            p.total === meta.total &&
            p.per_page === meta.per_page
          ) {
            return p;
          }
          return {
            ...p,
            current_page: meta.current_page,
            last_page: meta.last_page,
            total: meta.total,
            per_page: meta.per_page,
          };
        });
      }
    } finally {
      setPageLoading(false);
      setTableLoading(false);
    }
  };

  /* ================= EFFECTS ================= */
  useEffect(() => {
    if (canViewPage) fetchOvertime(true);
  }, [canViewPage]);

  useEffect(() => {
    if (canViewPage) fetchOvertime(false);
  }, [filters, debouncedSearch, pagination.current_page]);

  /* ================= FILTER HANDLER ================= */
  const handleFilterChange = (key, value) => {
    if (key === "__open_from__") {
      if (canUseFromDate) setFromAnchor(value);
      return;
    }
    if (key === "__open_to__") {
      if (canUseToDate) setToAnchor(value);
      return;
    }

    if (key === "from") key = "start_date";
    if (key === "to") key = "end_date";

    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  /* ================= TABLE ================= */
  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "username", align: "center" },
      {
        Header: "Date",
        accessor: "attendance_date",
        align: "left",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Expected Time",
        accessor: "expected_minutes",
        align: "left",
        Cell: ({ value }) => formatMinutesToHours(value),
      },
      {
        Header: "Reason",
        accessor: "reason",
        align: "left",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Status",
        accessor: "status",
        align: "left",
        Cell: ({ row }) => {
          const statusInfo = getLeaveStatusInfo(row.original.status);
          return (
            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <Icon
                sx={{
                  fontSize: "1rem",
                  color: (theme) => theme.palette[statusInfo.color].main,
                }}
              >
                {statusInfo.icon}
              </Icon>
              <SoftTypography
                variant="button"
                fontWeight="medium"
                color={statusInfo.color}
                sx={{ textTransform: "capitalize", fontSize: "0.8rem" }}
              >
                {statusInfo.text}
              </SoftTypography>
            </SoftBox>
          );
        },
      },
      {
        Header: "Status Reason",
        accessor: "status_reason",
        align: "left",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Approved By",
        accessor: "approved_by",
        align: "left",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Requested At",
        accessor: "requested_at",
        align: "left",
        Cell: ({ value }) => formatDateTime(value),
      },
      {
        Header: "Approved At",
        accessor: "approved_at",
        align: "left",
        Cell: ({ value }) => (value ? formatDateTime(value) : "N/A"),
      },
      { Header: "Actions", accessor: "actions", align: "left" },
    ],
    [formatDate, formatMinutesToHours, formatDateTime]
  );

  const rows = useMemo(() => {
    if (!Array.isArray(overtimeRequests)) return [];

    return overtimeRequests.map((req) => {
      const isPending = req.status === "pending";
      const isLoading = actionLoading === req.id;

      return {
        // username: req?.user?.name || "N/A",
        username: `${req?.user?.name || ""} ${req?.user?.last_name || ""}`.trim() || "N/A",
        attendance_date: req.attendance_date,
        expected_minutes: req.expected_minutes,
        reason: req.reason,
        status: req.status,
        status_reason: req.status_reason,
        approved_by: req?.approver?.name || "N/A",
        requested_at: req.requested_at || req.created_at,
        approved_at: req.approved_at,

        actions: isPending && (req.can_approve || req.can_reject) ? (
          <SoftBox display="flex" justifyContent="center" gap={1}>
            {req.can_approve && (
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  disabled={isLoading}
                  onClick={() => openApprove(req.id)}
                  sx={{
                    bgcolor: "success.main",
                    color: "#fff",
                    "&:hover": { bgcolor: "success.dark" },
                  }}
                >
                  {isLoading ? (
                    // <CircularProgress size={16} color="inherit" />
                    <FlagLoader />
                  ) : (
                    <CheckCircle fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {req.can_reject && (
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  disabled={isLoading}
                  onClick={() => openReject(req.id)}
                  sx={{
                    bgcolor: "error.main",
                    color: "#fff",
                    "&:hover": { bgcolor: "error.dark" },
                  }}
                >
                  {isLoading ? (
                    // <CircularProgress size={16} color="inherit" />
                    <FlagLoader />
                  ) : (
                    <Cancel fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </SoftBox>
        ) : (
          "-"
        ),
      };
    });
  }, [overtimeRequests, actionLoading]);

  const getPaginationRange = () => {
    const total = pagination.last_page;
    const current = pagination.current_page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  /* ================= GUARD ================= */
  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <CircularProgress /> */}
        <FlagLoader />

      </SoftBox>
    );
  }

  /* ================= UI ================= */
  return (
    <Card>
      <SoftBox py={1}>
        {/* ✅ FILTERS – now passing the new props */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchSubBranches={fetchSubBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters={true}
          pageContext="Attendance.Overtime.Request"
        />

        {/* HEADER */}
        <Grid container alignItems="center" justifyContent="space-between" mt={1} mb={-1}>
          <SoftTypography variant="h6" color="text">
            Overtime Requests ({pagination.total})
          </SoftTypography>
          {canCreateOvertime && (
            <SoftButton
              color="info"
              variant="gradient"
              startIcon={<AddIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              Add Overtime
            </SoftButton>
          )}
        </Grid>

        {/* TABLE */}
        {tableLoading ? (
          <SoftBox py={4} textAlign="center">
            {/* <CircularProgress /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            entriesPerPage={false}
            canSearch={false}
            showTotalEntries
          />
        )}

        {/* PAGINATION */}
        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              {/* {Array.from({ length: pagination.last_page }, (_, i) => (
                <SoftPagination
                  key={i}
                  item
                  active={pagination.current_page === i + 1}
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      current_page: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </SoftPagination>
              ))} */}



              {/* FIRST */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({ ...p, current_page: 1 }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                «
              </SoftPagination>

              {/* PREV */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page - 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ‹
              </SoftPagination>

              {/* NUMBERS */}
              {getPaginationRange().map((item, i) => {
                if (item === "...") {
                  return (
                    <SoftPagination key={i} item disabled>
                      ...
                    </SoftPagination>
                  );
                }

                return (
                  <SoftPagination
                    key={i}
                    item
                    active={pagination.current_page === item}
                    onClick={() => {
                      setPagination((p) => ({
                        ...p,
                        current_page: item,
                      }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {item}
                  </SoftPagination>
                );
              })}

              {/* NEXT */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page + 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ›
              </SoftPagination>

              {/* LAST */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: pagination.last_page,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                »
              </SoftPagination>


            </SoftPagination>
          </SoftBox>
        )}
      </SoftBox>

      {/* FROM DATE */}
      {canUseFromDate && (
        <Popover
          open={Boolean(fromAnchor)}
          anchorEl={fromAnchor}
          onClose={() => setFromAnchor(null)}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              onChange={(d) => {
                handleFilterChange("from", d.format("YYYY-MM-DD"));
                setFromAnchor(null);
              }}
            />
          </LocalizationProvider>
        </Popover>
      )}

      {/* TO DATE */}
      {canUseToDate && (
        <Popover open={Boolean(toAnchor)} anchorEl={toAnchor} onClose={() => setToAnchor(null)}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              onChange={(d) => {
                handleFilterChange("to", d.format("YYYY-MM-DD"));
                setToAnchor(null);
              }}
            />
          </LocalizationProvider>
        </Popover>
      )}

      {/* DRAWER */}
      {canCreateOvertime && (
        <OvertimeDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            fetchOvertime(true);
          }}
        />
      )}

      {/* APPROVE DIALOG */}
      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Overtime</DialogTitle>
        <DialogContent>
          <SoftBox display="flex" flexDirection="row" gap={2}>
            <SoftInput
              fullWidth
              multiline
              rows={3}
              label="Approve Reason"
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
            />
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="gradient" color="error" onClick={() => setOpenApproveDialog(false)}>
            Cancel
          </SoftButton>
          <SoftButton
            color="info"
            disabled={!approveReason.trim()}
            onClick={handleApproveConfirm}
            loading={actionLoading}
            variant="gradient"
          >
            Approve
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* REJECT DIALOG */}
      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Overtime</DialogTitle>
        <DialogContent>
          <SoftInput
            fullWidth
            multiline
            rows={3}
            label="Reject Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="error" onClick={() => setOpenRejectDialog(false)}>
            Cancel
          </SoftButton>
          <SoftButton
            color="info"
            disabled={!rejectReason.trim()}
            onClick={handleRejectConfirm}
            loading={actionLoading}
            variant="gradient"
          >
            Reject
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default OvertimeRequest;
